import Panel from './panel';

/**
 * Shows a list of railways and GTFS bus routes, and lets the user pick which
 * ones run vehicles.
 *
 * 두 종류를 한 목록에 담는다. 철도는 `map.railways`(간선 단위로 묶는다), 버스는
 * `map.gtfs.*.routes` 다. 버스 배포에는 철도가 없어서 예전에는 이 패널이 'all'
 * 한 줄만 띄웠다 - 필터가 있는데 고를 게 없었다.
 */
export default class extends Panel {

    constructor(options) {
        super(Object.assign({
            className: 'railway-panel',
            modal: true
        }, options));
    }

    addTo(map) {
        const me = this,
            // getAll() hands back a live iterator, so materialize it: the list is
            // walked several times below and a spent iterator yields nothing.
            railways = [...map.railways.getAll()].filter(({id}) => !id.startsWith('Base.')),
            // A branch carries its trunk's ID as a prefix (Seoul.Line2Seongsu ->
            // Seoul.Line2). Only trunks get a row; toggling one takes its branches
            // with it, so the list reads as the lines a rider knows.
            railGroups = railways
                .filter(railway => !railways.some(other => other !== railway && railway.id.startsWith(other.id)))
                .map(railway => ({
                    key: `rail-${railway.id}`,
                    title: map.getLocalizedRailwayTitle(railway),
                    color: railway.color,
                    ids: railways.filter(({id}) => id.startsWith(railway.id)).map(({id}) => id),
                    isOn: () => map.isRailwayVisible(railway.id),
                    set: visible => map.setRailwayVisibility(
                        railways.filter(({id}) => id.startsWith(railway.id)).map(({id}) => id), visible)
                }))
                .sort((a, b) => a.title.localeCompare(b.title, map.lang, {numeric: true})),
            busGroups = [...map.gtfs.values()].flatMap(gtfs => [...gtfs.routes.getAll()]
                .map(route => ({
                    key: `bus-${gtfs.id}-${route.id}`,
                    title: route.shortName || route.longName || route.id,
                    color: route.color || gtfs.color,
                    gtfsId: gtfs.id,
                    routeId: route.id,
                    isOn: () => map.isBusRouteVisible(gtfs.id, route.id),
                    set: visible => map.setBusRouteVisibility(gtfs.id, route.id, visible)
                })))
                .sort((a, b) => a.title.localeCompare(b.title, map.lang, {numeric: true})),
            // ⚠️ **키를 CSS id 로 그대로 쓰면 안 된다.** GTFS 노선 ID 는 숫자로
            // 시작하는 문자열이라(`204000048`) `#204000048-railway` 가 잘못된
            // 선택자다 - querySelector 가 던진다. 접두사와 치환을 같이 건다.
            safe = key => key.replace(/[^\w-]/g, '-'),
            groups = [...railGroups, ...busGroups],
            row = (key, color, title) => [
                `<div id="${safe(key)}-railway" class="railway-row">`,
                '<div class="railway-check"></div>',
                `<div class="line-strip" style="background-color: ${color};"></div>`,
                `<div>${title}</div>`,
                '<div class="railway-count"></div>',
                '</div>'
            ].join('');

        super.addTo(map)
            .setTitle(map.dict['railways'])
            .setHTML([
                row('all', 'transparent', map.dict['all-railways']),
                ...groups.map(({key, color, title}) => row(key, color, title))
            ].join(''));

        const cell = (key, cls) => me._container.querySelector(`#${safe(key)}-railway .${cls}`),
            runningBuses = () => {
                const n = new Map();

                for (const gtfs of map.gtfs.values()) {
                    for (const bus of gtfs.activeBusLookup.values()) {
                        const k = `bus-${gtfs.id}-${bus.trip.route}`;

                        n.set(k, (n.get(k) || 0) + 1);
                    }
                }
                return n;
            },
            refreshCounts = () => {
                const running = new Map(),
                    buses = runningBuses();

                for (const {r} of map.activeTrainLookup.values()) {
                    running.set(r.id, (running.get(r.id) || 0) + 1);
                }
                for (const group of railGroups) {
                    cell(group.key, 'railway-count').innerHTML =
                        group.ids.reduce((sum, id) => sum + (running.get(id) || 0), 0) || '';
                }
                for (const group of busGroups) {
                    cell(group.key, 'railway-count').innerHTML = buses.get(group.key) || '';
                }
                cell('all', 'railway-count').innerHTML =
                    map.activeTrainLookup.size + [...buses.values()].reduce((a, b) => a + b, 0) || '';
            },
            refresh = () => {
                for (const group of groups) {
                    cell(group.key, 'railway-check').classList.toggle('railway-check-on', group.isOn());
                }
                cell('all', 'railway-check').classList.toggle('railway-check-on', groups.every(g => g.isOn()));
                refreshCounts();
            };

        for (const group of groups) {
            cell(group.key, 'railway-check').parentNode.addEventListener('click', () => {
                group.set(!group.isOn());
                refresh();
            });
        }
        cell('all', 'railway-check').parentNode.addEventListener('click', () => {
            // 하나라도 꺼져 있으면 전부 켜고, 다 켜져 있으면 전부 끈다.
            const visible = !groups.every(g => g.isOn());

            for (const group of groups) {
                group.set(visible);
            }
            refresh();
        });
        refresh();
        // Running counts move on their own, so keep them ticking while the panel is up.
        me._countInterval = setInterval(refreshCounts, 5000);

        return me;
    }

    remove() {
        clearInterval(this._countInterval);

        return super.remove();
    }

}
